import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { formatDate } from "date-fns";
import { IdCard, Pencil } from "lucide-react";
import { StateLicensesForm } from "./StateLicensesForm";

type StateLicense = {
  index: number;
  value: unknown;
};
type StateLicenseValue = {
  state?: string | null;
  license_number?: string | null;
  expiration_date?: string | Date | null;
};

export function StateLicensesCard({
  editable,
  isEditing,
  onEditChange,
  stateLicenses,
  imlcLicense,
  userId,
  isProviderHidden = false,
}: {
  editable: boolean;
  isEditing: boolean;
  onEditChange: (editing: boolean) => void;
  stateLicenses: StateLicense[];
  imlcLicense: {
    has_license: boolean;
    state_codes: string[];
  } | null;
  userId: string;
  isProviderHidden?: boolean;
}) {
  return (
    <Card
      className={cn("mt-4", {
        "shadow-none border-none": isEditing,
      })}
    >
      <CardHeader className="pb-1">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">State Licenses</h3>
          {isEditing || !editable ? null : (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onEditChange(true)}
            >
              <Pencil className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {isEditing ? (
          <StateLicensesForm
            stateLicenses={stateLicenses}
            imlcLicense={imlcLicense}
            userId={userId}
            onSave={() => onEditChange(false)}
          />
        ) : (
          <>
            {imlcLicense?.has_license ? (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <IdCard className="h-5 w-5 text-gray-500" />
                  <div>
                    <div className="font-semibold">IMLC License</div>
                    <div className="text-sm text-gray-600">
                      {imlcLicense.state_codes.join(", ")}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3 mb-2">
                <IdCard className="h-5 w-5 text-gray-500" />
                <div className="font-semibold">No IMLC</div>
              </div>
            )}
            {stateLicenses.map((license, index) => {
              const value = license.value as StateLicenseValue;
              return (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <IdCard className="h-5 w-5 text-gray-500" />
                    <div>
                      <div className="font-semibold">
                        {value.state} Medical License
                      </div>
                      {isProviderHidden ? (
                        <div className="text-sm text-gray-400">
                          License #: Hidden
                        </div>
                      ) : (
                        <div className="text-sm text-gray-600">
                          License #: {value.license_number}
                        </div>
                      )}
                      {value.expiration_date ? (
                        <div className="text-sm text-gray-500">
                          Exp. {formatDate(value.expiration_date, "MMM yyyy")}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })}
            {stateLicenses.length === 0 && (
              <div className="text-center text-gray-500">
                No state licenses added yet.
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
